import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { Button } from '@/components/ui/Button'
import {
  PageError,
  PageLoading,
  PageNotFound,
} from '@/components/ui/PageAsyncState'
import { StatusBadge, type BadgeTone } from '@/components/ui/StatusBadge'
import {
  ProtoExcelTable,
  type ProtoColumn,
} from '@/components/table/ProtoExcelTable'
import { computeExplosionRunGroupCounts } from '@/features/explosions/explosionRunDetailMetrics'
import {
  explosionService,
  type BomVersionUsed,
  type ExplosionLineStatus,
  type ExplosionRunDetail,
  type ExplosionRunResult,
} from '@/services/explosions'
import { variantService } from '@/services/variants'

const SECTION_TONE_CLASS = {
  ok: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  warn: 'border-amber-200 bg-amber-50 text-amber-900',
  danger: 'border-red-200 bg-red-50 text-red-900',
  muted: 'border-slate-200 bg-slate-50 text-slate-700',
} as const

const LINE_STATUS_TONE: Record<ExplosionLineStatus, BadgeTone> = {
  OK: 'success',
  NEEDS_MAPPING: 'warning',
  NO_BOM: 'danger',
  PACKAGING_OPTION_NOT_DEFINED: 'neutral',
}

function Section({
  title,
  tone,
  badge,
  children,
}: {
  title: string
  tone: keyof typeof SECTION_TONE_CLASS
  badge?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="mb-4 overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
      <div
        className={`flex flex-wrap items-center gap-2 border-b px-3 py-2 text-xs font-semibold tracking-wide uppercase ${SECTION_TONE_CLASS[tone]}`}
      >
        <span>{title}</span>
        {badge}
      </div>
      <div className="p-3">{children}</div>
    </section>
  )
}

function asDetailRecord(detail: unknown): Record<string, unknown> {
  return detail && typeof detail === 'object'
    ? (detail as Record<string, unknown>)
    : {}
}

export function ExplosionRunDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [run, setRun] = useState<ExplosionRunDetail | null>(null)
  const [variantCodes, setVariantCodes] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      if (!id) return
      setLoading(true)
      setError(null)
      try {
        const detail = await explosionService.getById(id)
        if (!detail) {
          setRun(null)
          setError('Run không tồn tại')
          return
        }
        setRun(detail)
        const ids = new Set<string>()
        for (const r of detail.results) {
          if (r.variant_id) ids.add(r.variant_id)
        }
        const used = Array.isArray(detail.bom_versions_used)
          ? (detail.bom_versions_used as BomVersionUsed[])
          : []
        for (const u of used) ids.add(u.variant_id)
        const entries = await Promise.all(
          [...ids].map(async (vid) => {
            const v = await variantService.getById(vid)
            return [vid, v?.variantCode ?? vid] as const
          }),
        )
        setVariantCodes(Object.fromEntries(entries))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Không tải được run')
      } finally {
        setLoading(false)
      }
    })()
  }, [id])

  const groups = useMemo(() => {
    const byStatus: Record<ExplosionLineStatus, ExplosionRunResult[]> = {
      OK: [],
      NEEDS_MAPPING: [],
      NO_BOM: [],
      PACKAGING_OPTION_NOT_DEFINED: [],
    }
    if (!run) return byStatus
    for (const r of run.results) {
      byStatus[r.line_status].push(r)
    }
    return byStatus
  }, [run])

  const groupCounts = useMemo(
    () => computeExplosionRunGroupCounts(run?.results ?? []),
    [run],
  )

  const bomVersions = useMemo(() => {
    if (!run || !Array.isArray(run.bom_versions_used)) return []
    return run.bom_versions_used as BomVersionUsed[]
  }, [run])

  const okColumns = useMemo<ProtoColumn<ExplosionRunResult>[]>(
    () => [
      {
        id: 'name',
        header: 'Tên vật tư',
        width: 260,
        sortValue: (r) => `${r.material_name ?? ''} ${r.material_code ?? ''}`,
        cell: (r) => <span className="font-semibold text-slate-800">{r.material_name ?? '—'}</span>,
      },
      {
        id: 'uom',
        header: 'ĐVT',
        width: 80,
        sortValue: (r) => r.uom ?? '',
        cell: (r) => r.uom ?? '—',
      },
      {
        id: 'qty',
        header: 'qty_gross',
        width: 100,
        align: 'right',
        sortValue: (r) => r.qty_gross ?? 0,
        cell: (r) => (
          <span className="tabular-nums font-mono">{r.qty_gross ?? '—'}</span>
        ),
      },
    ],
    [],
  )

  if (loading) {
    return <PageLoading label="Đang tải run…" />
  }

  if (!run) {
    return (
      <PageNotFound
        title="Không tìm thấy run"
        description={error ?? 'Run không tồn tại'}
        action={
          <Button
            variant="secondary"
            onClick={() => navigate('/purchasing/explosions')}
          >
            Về danh sách
          </Button>
        }
      />
    )
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Gross Explosion', to: '/purchasing/explosions' },
          { label: run.id },
        ]}
      />
      <PageHeader
        title="Kết quả Gross Explosion"
        subtitle={`Snapshot · ${new Date(run.ran_at).toLocaleString('vi-VN')}${
          run.ran_by ? ` · ${run.ran_by}` : ''
        }`}
      />

      {error ? <PageError message={error} /> : null}

      <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
        <KpiCard label="OK" value={groupCounts.OK} tone="success" />
        <KpiCard
          label="NEEDS_MAPPING"
          value={groupCounts.NEEDS_MAPPING}
          tone={groupCounts.NEEDS_MAPPING ? 'warning' : 'default'}
        />
        <KpiCard
          label="NO_BOM"
          value={groupCounts.NO_BOM}
          tone={groupCounts.NO_BOM ? 'danger' : 'default'}
        />
        <KpiCard
          label="PACKAGING_OPTION_NOT_DEFINED"
          value={groupCounts.PACKAGING_OPTION_NOT_DEFINED}
        />
      </div>

      <section className="mb-4 overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-xs shadow-[var(--shadow-level-1)]">
        <h3 className="mb-2 font-semibold text-slate-700">
          BOM versions dùng (snapshot)
        </h3>
        {bomVersions.length === 0 ? (
          <p className="text-slate-400">
            Không có BOM nào được dùng (toàn NO_BOM).
          </p>
        ) : (
          <ul className="space-y-1 font-mono">
            {bomVersions.map((u, i) => (
              <li key={`${u.variant_id}-${u.bom_id}-${i}`}>
                Variant{' '}
                <span className="font-semibold">
                  {variantCodes[u.variant_id] ?? u.variant_id}
                </span>{' '}
                →{' '}
                <Link
                  className="text-[var(--color-link)] hover:underline"
                  to={`/purchasing/boms/${u.bom_id}`}
                >
                  {u.bom_id}
                </Link>{' '}
                ({u.version_label} / seq {u.version_seq})
              </li>
            ))}
          </ul>
        )}
      </section>

      <Section
        title={`OK — tổng nhu cầu vật tư (${groups.OK.length})`}
        tone="ok"
        badge={
          <StatusBadge
            tone={LINE_STATUS_TONE.OK}
            label="OK"
            showDot={false}
          />
        }
      >
        {groups.OK.length === 0 ? (
          <p className="text-xs text-slate-400">Không có dòng OK.</p>
        ) : (
          <ProtoExcelTable
            columns={okColumns}
            rows={groups.OK}
            rowKey={(r) => r.id}
            searchPlaceholder="Tìm mã / tên…"
            exportName={`explosion-${run.id}-ok`}
            stickyLeftColumnIds={['code']}
            showRowNumbers
            emptyMessage="Không có dòng OK."
          />
        )}
      </Section>

      <Section
        title={`NEEDS_MAPPING (${groups.NEEDS_MAPPING.length})`}
        tone="warn"
        badge={
          <StatusBadge
            tone={LINE_STATUS_TONE.NEEDS_MAPPING}
            label="NEEDS_MAPPING"
            showDot={false}
          />
        }
      >
        {groups.NEEDS_MAPPING.length === 0 ? (
          <p className="text-xs text-slate-400">Không có.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {groups.NEEDS_MAPPING.map((r) => (
              <li key={r.id}>
                <span className="mr-1 rounded bg-amber-200 px-1 text-xs font-semibold uppercase">
                  {r.source_kind ?? '?'}
                </span>
                line {r.source_line_id ?? '—'}
                {r.variant_id
                  ? ` · ${variantCodes[r.variant_id] ?? r.variant_id}`
                  : ''}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title={`NO_BOM (${groups.NO_BOM.length})`}
        tone="danger"
        badge={
          <StatusBadge
            tone={LINE_STATUS_TONE.NO_BOM}
            label="NO_BOM"
            showDot={false}
          />
        }
      >
        {groups.NO_BOM.length === 0 ? (
          <p className="text-xs text-slate-400">Không có.</p>
        ) : (
          <ul className="space-y-1 text-xs font-mono">
            {groups.NO_BOM.map((r) => (
              <li key={r.id}>
                {r.variant_id
                  ? (variantCodes[r.variant_id] ?? r.variant_id)
                  : '—'}
                {r.detail && typeof r.detail === 'object' ? (
                  <span className="ml-2 font-sans text-slate-500">
                    {JSON.stringify(r.detail)}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title={`PACKAGING_OPTION_NOT_DEFINED (${groups.PACKAGING_OPTION_NOT_DEFINED.length})`}
        tone="muted"
        badge={
          <StatusBadge
            tone={LINE_STATUS_TONE.PACKAGING_OPTION_NOT_DEFINED}
            label="PACKAGING_OPTION_NOT_DEFINED"
            showDot={false}
          />
        }
      >
        {groups.PACKAGING_OPTION_NOT_DEFINED.length === 0 ? (
          <p className="text-xs text-slate-400">Không có.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {groups.PACKAGING_OPTION_NOT_DEFINED.map((r) => {
              const d = asDetailRecord(r.detail)
              return (
                <li key={r.id}>
                  {r.variant_id
                    ? (variantCodes[r.variant_id] ?? r.variant_id)
                    : '—'}{' '}
                  · {String(d.assembly_type ?? '?')}+
                  {String(d.ista_level ?? '?')}
                </li>
              )
            })}
          </ul>
        )}
      </Section>
    </div>
  )
}
